/**
 * WebGL quality tiers: one detection at startup, one preset table, one hook.
 *
 * The current tier lives in the app store (`useAppStore`), so `degradeQuality()` can lower
 * it at runtime when a scene's `PerformanceMonitor` reports sustained low FPS.
 *
 * Import cycle: `store.ts` calls `detectQualityTier()` while it initialises, and
 * `useQuality()` here reads the store. That is safe because `detectQualityTier` is a
 * hoisted function declaration that touches no module-level state. Keep it that way.
 *
 * @module
 */
import { qualityOverride } from './flags'
import { useAppStore } from './store'

/** Rendering budget, from cheapest to richest. */
export type QualityTier = 'low' | 'medium' | 'high'

/** What each tier allows. Scenes read these through {@link useQuality}. */
export interface QualityPreset {
  /** Device-pixel-ratio clamp for the R3F `<Canvas dpr>`: `[min, max]`. */
  dpr: [min: number, max: number]
  /** Multiply particle counts by this, e.g. `Math.round(60_000 * particleScale)`. */
  particleScale: number
  /**
   * Multiply point and dot sizes by this, so fewer particles keep the same visual density
   * and silhouette. It is roughly `1 / sqrt(particleScale)`, kept a little lower so the
   * halftone stays airy. Lower tiers must look as designed, not sparse.
   */
  pointSizeScale: number
  /** Whether to mount an `<EffectComposer>` (N8AO, grading, ...). */
  postprocessing: boolean
  /**
   * MSAA samples for `<EffectComposer multisampling>`. On light backgrounds aliasing shows,
   * so a composer must always pass this (with SMAA as the fallback). 0 when post is off.
   */
  multisampling: number
  /**
   * Transmission render-target size in px. Pass it as `resolution` to drei's
   * `<MeshTransmissionMaterial>`. For a plain `MeshPhysicalMaterial`, convert it to
   * `gl.transmissionResolutionScale` (see docs/ARCHITECTURE.md).
   */
  transmissionResolution: number
  /** Whether shadow maps are allowed. `SceneCanvas` maps `shadows` to PCF shadows. */
  shadows: boolean
}

/** A preset plus the tier it belongs to, as returned by {@link useQuality}. */
export interface QualitySettings extends QualityPreset {
  tier: QualityTier
}

/**
 * The preset table. Tune budgets here, never inside sections.
 *
 * Lower tiers cut *cost*, not *beauty*. Every tier keeps animating, keeps native MSAA
 * (`antialias: true`, which is nearly free on the tile-based GPUs in phones), and renders
 * sharp enough for a 3× phone screen. They drop the expensive invisible work first:
 * full-screen post passes, shadow maps, transmission resolution and raw particle counts
 * (with `pointSizeScale` making up the density). See DESIGN.md §7, "Mobile is first-class".
 */
export const QUALITY_PRESETS: Readonly<Record<QualityTier, Readonly<QualityPreset>>> = {
  low: {
    dpr: [1, 1.5],
    particleScale: 0.35,
    pointSizeScale: 1.6,
    postprocessing: false,
    multisampling: 0,
    transmissionResolution: 384,
    shadows: false,
  },
  medium: {
    dpr: [1, 1.75],
    particleScale: 0.6,
    pointSizeScale: 1.25,
    postprocessing: true,
    multisampling: 4,
    transmissionResolution: 768,
    shadows: false,
  },
  high: {
    dpr: [1, 2],
    particleScale: 1,
    pointSizeScale: 1,
    postprocessing: true,
    multisampling: 8,
    transmissionResolution: 1024,
    shadows: true,
  },
}

/** Stable objects per tier, so `useQuality()` is safe to use in dependency arrays. */
const SETTINGS: Readonly<Record<QualityTier, QualitySettings>> = {
  low: { tier: 'low', ...QUALITY_PRESETS.low },
  medium: { tier: 'medium', ...QUALITY_PRESETS.medium },
  high: { tier: 'high', ...QUALITY_PRESETS.high },
}

/**
 * Picks a starting tier from the device. `?quality=` wins when present.
 *
 * - **low**: a coarse pointer with ≤2 GB `deviceMemory` (genuinely budget phones)
 * - **medium**: any other coarse pointer, a viewport narrower than 768px, or ≤4 cores
 * - **high**: everything else
 *
 * Phones start at **medium**, not low. Their CPU core count says little about their GPU,
 * and Safari reports neither real cores nor `deviceMemory`. So we start on a rich preset and
 * let `SceneCanvas` *measure*. It lowers resolution first and steps down a tier only if the
 * frame rate still can't hold. Missing APIs count as "not constrained".
 */
export function detectQualityTier(): QualityTier {
  if (qualityOverride) return qualityOverride
  if (typeof window === 'undefined') return 'high'

  const nav = navigator as Navigator & { deviceMemory?: number }
  const cores = nav.hardwareConcurrency || 8
  const memoryGb = nav.deviceMemory ?? 8
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches
  const narrowViewport = window.innerWidth < 768

  if (coarsePointer) return memoryGb <= 2 ? 'low' : 'medium'
  if (narrowViewport || cores <= 4) return 'medium'
  return 'high'
}

/**
 * The current quality preset plus its `tier`. It re-renders when the tier degrades.
 *
 * @example
 * const { particleScale, postprocessing } = useQuality()
 * const count = Math.round(60_000 * particleScale)
 */
export function useQuality(): QualitySettings {
  const tier = useAppStore((state) => state.quality)
  return SETTINGS[tier]
}
